/*
Copyright (c) 2012-2023 Grab Taxi Holdings PTE LTD (GRAB), All Rights Reserved.
NOTICE: All information contained herein is, and remains the property of GRAB.
The intellectual and technical concepts contained herein are confidential,
proprietary and controlled by GRAB and may be covered by patents, patents in process,
and are protected by trade secret or copyright law.

You are strictly forbidden to copy, download, store (in any medium), transmit, disseminate,
adapt or change this material in any way unless prior written permission is obtained from GRAB.
Access to the source code contained herein is hereby forbidden to anyone except current GRAB
employees or contractors with binding Confidentiality and Non-disclosure agreements explicitly
covering such access.

The copyright notice above does not evidence any actual or intended publication or disclosure of
this source code, which includes information that is confidential and/or proprietary,
and is a trade secret, of GRAB.

ANY REPRODUCTION, MODIFICATION, DISTRIBUTION, PUBLIC PERFORMANCE, OR PUBLIC DISPLAY OF OR
THROUGH USE OF THIS SOURCE CODE WITHOUT THE EXPRESS WRITTEN CONSENT OF GRAB IS STRICTLY PROHIBITED,
AND IN VIOLATION OF APPLICABLE LAWS AND INTERNATIONAL TREATIES. THE RECEIPT OR POSSESSION OF THIS
SOURCE CODE AND/OR RELATED INFORMATION DOES NOT CONVEY OR IMPLY ANY RIGHTS TO REPRODUCE,
DISCLOSE OR DISTRIBUTE ITS CONTENTS, OR TO MANUFACTURE, USE, OR SELL ANYTHING THAT IT MAY DESCRIBE,
IN WHOLE OR IN PART.
*/
import { getApiUrl } from "constants/domain"
import update from "immutability-helper"
import { AxiosAction } from "types/api"
import { GetFeatureResponse, GetFeaturesRequest } from "types/delv-platform-api"
import { getActionFail, getActionSuccess } from "store/duck/utils/actionUtils"
import { FEConfigData, GlobalState, Messages } from "./global.type"
import { createReducer } from "../create-reducer"

const NAMESPACE = "GLOBAL"

// actions
export const actions = {
  SHOW_LOADING: `${NAMESPACE}/SHOW_LOADING`,
  HIDE_LOADING: `${NAMESPACE}/HIDE_LOADING`,
  SET_MESSAGES: `${NAMESPACE}/SET_MESSAGES`,
  LOAD_CONFIG: `${NAMESPACE}/LOAD_CONFIG`,
  DELV_PLATFORM_FEATURE_FLAGS: `${NAMESPACE}/DELV_PLATFORM_FEATURE_FLAGS`, // fetch feature flags from delv-platform-api
  CLEAR_DELV_PLATFORM_FEATURE_FLAGS: `${NAMESPACE}/CLEAR_DELV_PLATFORM_FEATURE_FLAGS`,
  TOGGLE_CHUNK_ERROR_MODAL: `${NAMESPACE}/TOGGLE_CHUNK_ERROR_MODAL`,
}

const apiUrl = getApiUrl()
const baseFoodMaxUrl = `${apiUrl}/food/merchant`
const baseDelvPlatformUrl = `${apiUrl}/delvplatformapi/merchant`

// action creators
export const actionCreator = {
  toggleChunkErrorModal: () => ({
    type: actions.TOGGLE_CHUNK_ERROR_MODAL,
  }),
  showLoading: () => ({
    type: actions.SHOW_LOADING,
  }),
  hideLoading: () => ({
    type: actions.HIDE_LOADING,
  }),
  setMessages: (messages: Messages) => ({
    type: actions.SET_MESSAGES,
    messages,
  }),
  loadConfig: (merchantID: string) => ({
    type: actions.LOAD_CONFIG,
    showError: true,
    payload: {
      merchantID,
      request: {
        url: `${baseFoodMaxUrl}/v1/config`,
        method: "get",
        headers: {
          merchantID,
        },
      },
    },
  }),
  fetchDelvPlatformFeatureFlags: (
    payload: GetFeaturesRequest,
    merchantID: string,
    merchantGroupID: string,
  ) => ({
    type: actions.DELV_PLATFORM_FEATURE_FLAGS,
    payload: {
      request: {
        url: `${baseDelvPlatformUrl}/v1/features`,
        params: payload,
        headers: {
          merchantID,
          merchantGroupID,
        },
      },
    },
  }),
  clearDelvPlatformFeatureFlags: () => ({
    type: actions.CLEAR_DELV_PLATFORM_FEATURE_FLAGS,
  }),
}

type ActionCreatorTypes = {
  [K in keyof typeof actionCreator]: ReturnType<typeof actionCreator[K]>
}
export type ActionCreatorTypeList = ActionCreatorTypes[keyof ActionCreatorTypes]

// state
export const initialState = (): GlobalState => ({
  showChunkErrorModal: false,
  showGlobalLoading: false,
  messages: {
    success: "SUCCESS",
    error: "ERROR",
  },
  feConfig: {
    isLoading: false,
    data: undefined,
    forMerchantID: undefined,
  },
  delvPlatformFeatureFlags: {
    fetchStarted: false, // turn to "true" whenever fetching starts, and never turn back to "false"
    isLoading: false,
    featureFlags: {},
  },
})

// reducer
export const reducer = createReducer<
  GlobalState,
  string,
  ActionCreatorTypeList
>(initialState(), {
  [actions.TOGGLE_CHUNK_ERROR_MODAL]: state => {
    const nextState = update(state, {
      showChunkErrorModal: {
        $set: !state.showChunkErrorModal,
      },
    })

    return nextState
  },
  [actions.SHOW_LOADING]: state => {
    const nextState = update(state, {
      showGlobalLoading: {
        $set: true,
      },
    })

    return nextState
  },
  [actions.HIDE_LOADING]: state => {
    const nextState = update(state, {
      showGlobalLoading: {
        $set: false,
      },
    })

    return nextState
  },
  [actions.SET_MESSAGES]: (
    state: GlobalState,
    action: ActionCreatorTypes["setMessages"],
  ) => {
    const messages = action.messages

    const nextState = update(state, {
      messages: {
        $set: messages,
      },
    })

    return nextState
  },
  [actions.LOAD_CONFIG]: (
    state: GlobalState,
    action: ActionCreatorTypes["loadConfig"],
  ) => {
    return update(state, {
      feConfig: {
        isLoading: { $set: true },
        data: { $set: undefined },
        forMerchantID: {
          $set: action.payload.merchantID,
        },
      },
    })
  },
  [getActionSuccess(actions.LOAD_CONFIG)]: (
    state: GlobalState,
    action: AxiosAction<{ config: FEConfigData }>,
  ) => {
    const data = action.payload.data.config
    const nextState = update(state, {
      feConfig: {
        isLoading: {
          $set: false,
        },
        data: {
          $set: data,
        },
      },
    })
    return nextState
  },
  [actions.DELV_PLATFORM_FEATURE_FLAGS]: (state: GlobalState) => {
    return update(state, {
      delvPlatformFeatureFlags: {
        fetchStarted: {
          $set: true,
        },
        isLoading: {
          $set: true,
        },
        error: {
          $set: undefined,
        },
      },
    })
  },
  [actions.CLEAR_DELV_PLATFORM_FEATURE_FLAGS]: (state: GlobalState) => {
    return update(state, {
      delvPlatformFeatureFlags: {
        fetchStarted: {
          $set: false,
        },
        isLoading: {
          $set: false,
        },
        featureFlags: {
          $set: {},
        },
        error: {
          $set: undefined,
        },
      },
    })
  },
  [getActionSuccess(actions.DELV_PLATFORM_FEATURE_FLAGS)]: (
    state: GlobalState,
    action: AxiosAction<GetFeatureResponse>,
  ) => {
    const data = action?.payload?.data ?? {}

    return update(state, {
      delvPlatformFeatureFlags: {
        isLoading: {
          $set: false,
        },
        featureFlags: {
          $set: data,
        },
        error: {
          $set: undefined,
        },
      },
    })
  },
  [getActionFail(actions.DELV_PLATFORM_FEATURE_FLAGS)]: (
    state: GlobalState,
    action: AxiosAction<GetFeatureResponse>,
  ) => {
    return update(state, {
      delvPlatformFeatureFlags: {
        isLoading: {
          $set: false,
        },
        featureFlags: {
          $set: {},
        },
        error: {
          $set: action?.error,
        },
      },
    })
  },
})
